<template lang="html">
  <div>
    <div v-if="!libraryLoaded">
      <div class="d-flex justify-content-center mb-3 mt-3">
        <b-spinner label="Loading library ..." />
      </div>
    </div>
    <div v-if="libraryLoaded">
      <b-row class="mb-5">
        <b-col>
          <p class="float-right small mt-1">Filter library by:</p>
        </b-col>
        <b-col>
          <b-form-select
            v-model="sortGenreSelected"
            :options="sortGenreOptions"
            size="sm"
            md="6"
          />
        </b-col>
        <b-col>
          <b-form-select
            v-model="sortMoodSelected"
            :options="sortMoodOptions"
            size="sm"
            md="6"
          />
        </b-col>
        <b-col>
          <b-form-select
            v-model="sortFavoriteSelected"
            :options="sortFavoriteOptions"
            size="sm"
            md="6"
          />
        </b-col>
        <b-col>
          <b-btn
            v-show="filtersUtilized"
            variant="light"
            size="sm"
            @click="resetSort"
          >
            Reset Filters <v-icon name="times-circle" />
          </b-btn>
        </b-col>
      </b-row>

      <b-table
        :items="libraryTracks"
        :sort-by.sync="sortBy"
        :fields="libraryTrackFields"
        responsive
        class="mt-3"
      >
        <template v-slot:cell(description)="data">
          <span>{{ data.item.genre }}, {{ data.item.mood }}</span>
        </template>
        <template v-slot:cell(duration)="data">
          <span>{{ (data.item.duration * 1000) | toMMSS }}</span>
        </template>
        <template v-slot:cell(favorites)="data">
          <p>
            <b-btn
              v-if="!userFavorites.includes(data.item.filename)"
              variant="light"
              @click="addTrackToUserFavorites(data.item.filename)"
            >
              <v-icon name="regular/heart" />
            </b-btn>
            <b-btn
              v-else
              variant="primary"
              @click="removeTrackToUserFavorites(data.item.filename)"
            >
              <v-icon name="heart" />
            </b-btn>
          </p>
          <span>
            <b-badge v-if="data.item.creator_favorite" variant="light">
              Top choice
            </b-badge>
          </span>
        </template>
        <template v-slot:cell(actions)="data">
          <p class="button-min">
            <b-btn
              v-if="preShowCreation"
              size="sm"
              variant="light"
              :disabled="$store.getters.restrictShowCreation"
              @click="addToNewShow(data.item)"
            >
              Create new show with track <v-icon name="plus" class="ml-1" />
            </b-btn>
            <b-btn
              v-else
              size="sm"
              variant="light"
              @click="addToShow(data.item)"
            >
              Import track <v-icon name="upload" class="ml-1" />
            </b-btn>
          </p>
          <p class="mb-0">
            <audio
              v-if="previewAudio == data.item.filename"
              controls
              autoplay
              controlsList="nodownload"
            >
              <source
                :src="
                  `https://cdn3.soundslides.com/audio/${data.item.source_id}/${data.item.filename}`
                "
                type="audio/mpeg"
              />
              Your browser does not support the audio element.
            </audio>
            <b-btn
              v-else
              size="sm"
              variant="light"
              @click="previewAudio = data.item.filename"
            >
              Preview <v-icon name="volume-up" class="ml-1" />
            </b-btn>
          </p>
        </template>
      </b-table>
      <b-btn size="sm" variant="outline-primary" @click="$emit('close')">
        Hide Library
      </b-btn>
    </div>
  </div>
</template>

<script>
import { db } from "@/fstore";
import filter from "lodash/filter";
import map from "lodash/map";
import uniq from "lodash/uniq";
import sortBy from "lodash/sortBy";

export default {
  name: "MusicLibrarySelector",
  filters: {
    toMMSS: function(s) {
      // Pad to 2 or 3 digits, default is 2
      function pad(n, z) {
        z = z || 2;
        return ("00" + n).slice(-z);
      }

      var ms = s % 1000;
      s = (s - ms) / 1000;
      var secs = s % 60;
      s = (s - secs) / 60;
      var mins = s % 60;

      return pad(mins) + ":" + pad(secs);
    }
  },
  props: {
    preShowCreation: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      libraryLoaded: false,
      libraryDoc: null,
      libraryInitialTracks: [],
      libraryTrackFields: [
        "title",
        "description",
        { key: "duration", sortable: true },
        "actions",
        "favorites"
      ],
      sortBy: "",
      audioObj: null,
      previewAudio: null,
      sortGenreSelected: null,
      sortGenreOptions: [],
      sortMoodSelected: null,
      sortMoodOptions: [],
      sortFavoriteSelected: null,
      sortFavoriteOptions: [
        { value: null, text: "Favorites" },
        { value: "site", text: "Site favorites" },
        { value: "user", text: "My favorites" },
        { value: null, text: "Show all" }
      ]
    };
  },
  computed: {
    libraryTracks() {
      var allTracks = this.libraryInitialTracks;
      if (this.sortGenreSelected) {
        allTracks = filter(allTracks, { genre: this.sortGenreSelected });
      }
      if (this.sortMoodSelected) {
        allTracks = filter(allTracks, { mood: this.sortMoodSelected });
      }
      if (this.sortFavoriteSelected) {
        if (this.sortFavoriteSelected == "site") {
          allTracks = filter(allTracks, { creator_favorite: true });
        }
        if (this.sortFavoriteSelected == "user") {
          allTracks = filter(allTracks, t => {
            return this.userFavorites.includes(t.filename);
          });
        }
      }
      return allTracks;
    },
    userFavorites() {
      return this.$store.state.user_doc.music_favorites || [];
    },
    filtersUtilized() {
      return (
        this.sortGenreSelected ||
        this.sortMoodSelected ||
        this.sortFavoriteSelected
      );
    }
  },
  watch: {
    sortGenreSelected() {
      this.previewAudio = null;
    },
    sortMoodSelected() {
      this.previewAudio = null;
    },
    sortFavoriteSelected() {
      this.previewAudio = null;
    }
  },
  mounted() {
    this.loadMusicLibrary();
  },
  methods: {
    addTrackToUserFavorites(filename) {
      this.$store.dispatch("userFavorites/addTrackToUserFavorites", filename);
    },
    removeTrackToUserFavorites(filename) {
      this.$store.dispatch(
        "userFavorites/removeTrackToUserFavorites",
        filename
      );
    },
    resetSort() {
      this.sortMoodSelected = null;
      this.sortGenreSelected = null;
      this.sortFavoriteSelected = null;
      this.sortBy = null;
      this.previewAudio = null;
    },
    performInitialSort() {
      // This sorts user favorites to the top when data first loads.
      this.libraryInitialTracks = sortBy(
        filter(this.libraryDoc, (s, k) => {
          if (k.startsWith("track_")) {
            var i = s;
            s.fieldKey = k;
            return i;
          }
        }),
        i => {
          return !this.userFavorites.includes(i.filename);
        }
      );
    },
    setSortOptions() {
      // Genres
      var genres = uniq(map(this.libraryTracks, "genre")).sort();
      genres.unshift({ value: null, text: "Genre" });
      genres.push({ value: null, text: "Show all" });
      this.sortGenreOptions = genres;
      // Mood
      var moods = uniq(map(this.libraryTracks, "mood")).sort();
      moods.unshift({ value: null, text: "Mood" });
      moods.push({ value: null, text: "Show all" });
      this.sortMoodOptions = moods;
    },
    addToNewShow(track) {
      console.log("addToNewShow", track);
      this.$store.dispatch("showCreation/newShowWithMusicTrack", track);
      this.previewAudio = null;
    },
    addToShow(track) {
      console.log("addToShow", track);
      /*
      var update = {};
      update["data.audio"] = {
        deployed: true,
        duration: track.duration,
        filename: track.filename,
        name: track.title,
        source_id: track.source_id,
        waveformData: true
      };
      update["staticMp3Deployed_" + track.fieldKey] = {
        duration: track.duration,
        filename: track.filename,
        key: "audio/" + track.source_id + "/" + track.filename,
        source_id: track.source_id
      };
      */
      this.$store.dispatch("show/updateAudioWithExistingTrack", track);
      this.previewAudio = null;
      this.$emit("close");
      console.log(track);
    },
    loadMusicLibrary() {
      var _v = this;
      var docRef = db.collection("music").doc("mainLibrary");
      docRef
        .get()
        .then(function(doc) {
          if (doc.exists) {
            _v.libraryDoc = doc.data();
            _v.libraryLoaded = true;
            _v.performInitialSort();
            _v.setSortOptions();
            console.log("libraryDoc", _v.libraryDoc);
          } else {
            // doc.data() will be undefined in this case
            console.log("No such document!");
          }
        })
        .catch(function(error) {
          console.log("Error getting document:", error);
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.button-min {
  min-width: 160px;
}
</style>
